/**
 * The pure roving-tabindex navigation model for the Calendar_View composite
 * widget (task 22.4, R10.11, R10.13, R18.6).
 *
 * The Calendar_View is a *composite widget* in the WAI-ARIA sense: a grid of
 * many focusable bars and milestone markers of which exactly one is in the tab
 * order at a time (roving tabindex). Tab moves into and out of the whole widget;
 * once inside, the arrow keys move a *roving* focus between the widget's items
 * without leaving it, and Enter or Space activates the focused item (opens the
 * Detail_Panel). This module is the pure decision layer: given the current
 * roving position and a key, it returns where focus should move or what should be
 * activated. It owns no DOM, no React state and no `tabindex` attribute — those
 * belong to the hook that consumes it — so the whole navigation contract is a
 * total function of its inputs and is unit-tested without a browser.
 *
 * ## The focus grid
 *
 * The widget's focusables are modelled as an ordered list of *lanes*, each lane
 * an ordered list of *items* laid out left to right in that Platform lane. A bar
 * and its milestone markers are distinct items in the same lane, ordered by their
 * horizontal position, so the arrow keys walk a lane's bars and markers in the
 * order the eye reads them. A {@link FocusPosition} is a `(lane, item)` pair.
 *
 * ## Key mapping (exactly the task's contract)
 *
 * - `ArrowRight` / `ArrowLeft` move within the current lane by one item, clamped
 *   at the lane's ends (no wrap): the roving focus never jumps to another lane on
 *   a horizontal arrow, matching "arrow keys move within a lane".
 * - `ArrowDown` / `ArrowUp` move across lanes, preserving the item index where the
 *   target lane is long enough and clamping to the target lane's last item where
 *   it is shorter, and clamped at the first and last lane (no wrap).
 * - `Enter` / `Space` (` `) activate the focused item.
 *
 * Home/End and PageUp/PageDown are intentionally *not* handled here: the task and
 * UI spec §9 specify only the four arrows plus Enter/Space, and adding further
 * keys would be an unrequested extension of the accessibility contract. See the
 * flagged decision in the task report.
 *
 * A lane with no items is a valid state (an empty Platform lane): it holds no
 * focusable item, so vertical navigation skips over it to the next non-empty
 * lane, and the model never returns a position pointing at an empty lane.
 */

/** One key the composite widget interprets. Anything else is ignored (returns `none`). */
export type CalendarKey =
  | 'ArrowRight'
  | 'ArrowLeft'
  | 'ArrowDown'
  | 'ArrowUp'
  | 'Enter'
  | ' ';

/** A `(lane, item)` coordinate into the focus grid. */
export interface FocusPosition {
  /** 0-based lane index. */
  readonly lane: number;
  /** 0-based item index within {@link FocusPosition.lane}. */
  readonly item: number;
}

/**
 * The shape of the focus grid the model reasons over: the number of focusable
 * items in each lane, in lane order. `laneItemCounts[l]` is the count of items in
 * lane `l`; a `0` denotes an empty lane, which carries no focusable item.
 */
export interface FocusGrid {
  readonly laneItemCounts: readonly number[];
}

/** What a key resolves to: move the roving focus, activate, or do nothing. */
export type CalendarKeyResult =
  | { readonly kind: 'move'; readonly to: FocusPosition }
  | { readonly kind: 'activate'; readonly at: FocusPosition }
  | { readonly kind: 'none' };

/** True when `position` points at a real, focusable item of `grid`. */
export function isFocusable(grid: FocusGrid, position: FocusPosition): boolean {
  const count = grid.laneItemCounts[position.lane];
  return count !== undefined && position.item >= 0 && position.item < count;
}

/**
 * The first focusable position of the grid — the initial roving target when the
 * widget is first entered — or `null` when the grid holds no focusable item at
 * all (every lane empty, or no lanes). The first non-empty lane's first item.
 */
export function firstFocusable(grid: FocusGrid): FocusPosition | null {
  for (let lane = 0; lane < grid.laneItemCounts.length; lane += 1) {
    if ((grid.laneItemCounts[lane] ?? 0) > 0) {
      return { lane, item: 0 };
    }
  }
  return null;
}

/**
 * Resolves a key press at `from` against `grid` into a navigation result.
 *
 * Pure and total: unhandled keys, and moves that would leave the grid, resolve to
 * a no-op (`none`) or a clamp rather than throwing, so the consuming hook can call
 * this on every key event without pre-filtering. When `from` is not itself a
 * focusable position (a stale coordinate after the grid shrank), the result is
 * `none`; the hook re-seeds focus in that case.
 */
export function resolveCalendarKey(
  grid: FocusGrid,
  from: FocusPosition,
  key: CalendarKey,
): CalendarKeyResult {
  if (!isFocusable(grid, from)) {
    return { kind: 'none' };
  }

  switch (key) {
    case 'Enter':
    case ' ':
      return { kind: 'activate', at: from };

    case 'ArrowRight':
      return horizontalMove(grid, from, 1);
    case 'ArrowLeft':
      return horizontalMove(grid, from, -1);

    case 'ArrowDown':
      return verticalMove(grid, from, 1);
    case 'ArrowUp':
      return verticalMove(grid, from, -1);
  }
}

/** Move one item within the current lane, clamped at the lane's ends (no wrap). */
function horizontalMove(
  grid: FocusGrid,
  from: FocusPosition,
  delta: 1 | -1,
): CalendarKeyResult {
  const count = grid.laneItemCounts[from.lane] ?? 0;
  const nextItem = from.item + delta;
  if (nextItem < 0 || nextItem >= count) {
    // At a lane edge a horizontal arrow does nothing — it never crosses lanes.
    return { kind: 'none' };
  }
  return { kind: 'move', to: { lane: from.lane, item: nextItem } };
}

/**
 * Move across lanes toward `delta`, skipping empty lanes and preserving the
 * column (item index) where the target lane is long enough, clamping to its last
 * item where it is shorter. Clamped at the first and last non-empty lane.
 */
function verticalMove(
  grid: FocusGrid,
  from: FocusPosition,
  delta: 1 | -1,
): CalendarKeyResult {
  for (let lane = from.lane + delta; lane >= 0 && lane < grid.laneItemCounts.length; lane += delta) {
    const count = grid.laneItemCounts[lane] ?? 0;
    if (count === 0) continue; // Skip empty lanes rather than landing on nothing.
    const item = Math.min(from.item, count - 1);
    return { kind: 'move', to: { lane, item } };
  }
  // No non-empty lane in that direction: the roving focus stays where it is.
  return { kind: 'none' };
}

/**
 * Clamps a possibly-stale position onto the nearest focusable position of `grid`,
 * or returns the grid's first focusable position, or `null` for an empty grid.
 *
 * Used by the hook when the grid changes shape under a held focus (a lane
 * collapsed, a filter narrowed the result): rather than lose focus into nowhere,
 * the roving position is snapped back onto a real item — the same lane's last
 * item if the lane shrank, else the first focusable item.
 */
export function clampPosition(grid: FocusGrid, position: FocusPosition): FocusPosition | null {
  const count = grid.laneItemCounts[position.lane];
  if (count !== undefined && count > 0) {
    return { lane: position.lane, item: Math.min(Math.max(position.item, 0), count - 1) };
  }
  return firstFocusable(grid);
}
